import { useLocation, useNavigate } from 'react-router-dom'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import {
  getNextPageText,
  getPreviousPageText,
  getResetToStepText,
  getReturnToStepText,
} from 'modules/payroll_bundle/components/PageFooter/_components/copies'
import { useResultConceptItemsTable } from 'modules/payroll_bundle/hooks/useResultConceptItemsTable'
import { comparePaths } from 'modules/payroll_bundle/lib/comparePaths'
import { PayrollStatus } from 'modules/payroll_bundle/lib/enums'
import pathForPolicyPeriodStatus from 'modules/payroll_bundle/lib/pathForPolicyPeriodStatus'
import {
  ChangeStatusModalActionTypes,
  usePayrollContext,
} from 'modules/payroll_bundle/lib/payrollContext'
import { PathSegment } from 'modules/payroll_bundle/lib/types'

import { usePolicyPeriodStatusForLocation } from 'modules/payroll_bundle/lib/usePolicyPeriodStatusForLocation'
import {
  useMatchOffCyclesPaymentUrl,
  useMatchOffcycleResultsUrl,
  useMatchOffcycleUrl,
} from './useMatchPayrollUrls'

/**
 * Determine what state we will naturally return to from the current state.
 * @param status The current status from which to check
 * @returns the previous natural state in the workflow
 */
function getDefaultPreviousStatus(status: PayrollStatus): PayrollStatus | null {
  switch (status) {
    case PayrollStatus.PAID:
      return PayrollStatus.PAYROLL_CALCULATION
    case PayrollStatus.PAYROLL_CALCULATION:
      return PayrollStatus.UNDER_REVIEW
    default:
      return null
  }
}

/**
 * Determine what state will naturally follow from the current state.
 * @param status The current status from which to check
 * @returns the next natural state in the workflow
 */
function getDefaultNextStatus(status: PayrollStatus): PayrollStatus | null {
  switch (status) {
    case PayrollStatus.PREPARATION:
    case PayrollStatus.SUPPLEMENTS_DEFINITION:
    case PayrollStatus.UNDER_REVIEW:
      return PayrollStatus.PAYROLL_CALCULATION
    case PayrollStatus.PAYROLL_CALCULATION:
      return PayrollStatus.PAID
    default:
      return null
  }
}

type CorrectPageFooterControlsProps = {
  status: PayrollStatus
  policyId: PathSegment
  periodId: PathSegment
}
function useCyclesActions({ status, policyId, periodId }: CorrectPageFooterControlsProps) {
  const { pathname: currentPath } = useLocation()
  const { payrollDispatch } = usePayrollContext()

  const previousStatus = getDefaultPreviousStatus(status)
  const handlePrevious = () => {
    if (previousStatus) {
      payrollDispatch({
        type: ChangeStatusModalActionTypes.SHOW_CHANGE_STATUS_MODAL,
        payload: previousStatus,
      })

      track(trackingEvents.payroll.userClicksOnPayrollPreviousCycle, {
        previous_status: previousStatus,
      })
    }
  }

  const nextStatus = getDefaultNextStatus(status)
  const handleContinue = () => {
    if (nextStatus)
      payrollDispatch({
        type: ChangeStatusModalActionTypes.SHOW_CHANGE_STATUS_MODAL,
        payload: nextStatus,
      })

    track(trackingEvents.payroll.userClicksOnPayrollContinueCycle, {
      next_status: nextStatus,
    })
  }

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const handleExit = () => {
    if (policyId) {
      navigate(navigation.payrollBundle.policy.dashboard.fullPath({ policyId }))

      track(trackingEvents.payroll.userClicksOnPayrollExitCycle)
    }
  }

  const { fetching, resultsTableData } = useResultConceptItemsTable(Number(periodId))
  // The result concepts are returned even if the file was unprocessed
  const currentResults = resultsTableData.filter((result) =>
    Object.keys(result.conceptItems).some((key) => key.endsWith(`policyPeriodId:${periodId}`))
  )

  const hasResults = currentResults.length > 0
  const isContinueDisabled = nextStatus == PayrollStatus.PAID && (fetching || !hasResults)

  const targetTabPath = pathForPolicyPeriodStatus({
    status,
    policyId,
    policyPeriodId: periodId,
    navigation,
  })

  const statusForTab = usePolicyPeriodStatusForLocation({
    path: currentPath,
    policyId,
    policyPeriodId: periodId,
    navigation,
  })

  const handleRevert = () => {
    if (!statusForTab) return
    payrollDispatch({
      type: ChangeStatusModalActionTypes.SHOW_CHANGE_STATUS_MODAL,
      payload: statusForTab,
    })
  }

  const handleReturnToCurrentStep = () => {
    if (policyId) {
      navigate(targetTabPath)
    }
  }

  const isCorrectTabForStatus: boolean = useUnsafeMemo(() => {
    const correctPathForCurrentStatus = pathForPolicyPeriodStatus({
      status,
      policyId: policyId,
      policyPeriodId: periodId,
      navigation,
    })

    return comparePaths(currentPath, correctPathForCurrentStatus)
  }, [currentPath, status, policyId, periodId, navigation])

  // Primary Action: Continue or Finish
  let primaryAction = nextStatus
    ? {
        disabled: isContinueDisabled,
        onClick: handleContinue,
        label: getNextPageText(status),
      }
    : {
        disabled: false,
        onClick: handleExit,
        label: i18n.t('payroll_bundle.policy_period.cycle_process.actions.finish_process'),
      }

  // Secondary Action: Exit or Previous
  let secondaryAction = previousStatus
    ? {
        disabled: false,
        onClick: handlePrevious,
        label: getPreviousPageText(status),
      }
    : {
        disabled: false,
        onClick: handleExit,
        label: i18n.t('payroll_bundle.policy_period.cycle_process.actions.exit_process'),
      }

  // Override actions if the current tab is not correct - show divergent options
  if (!isCorrectTabForStatus) {
    primaryAction = {
      disabled: false,
      onClick: handleReturnToCurrentStep,
      label: getReturnToStepText(),
    }

    secondaryAction = {
      disabled: false,
      onClick: handleRevert,
      label: getResetToStepText(),
    }
  }

  // Override actions if we are in offycles
  const dashboardUrl = navigation.payrollBundle.policy.dashboard.fullPath({ policyId })
  const isOffcyclePage = useMatchOffcycleUrl(policyId, periodId)
  const isOffcycleResultsUrl = useMatchOffcycleResultsUrl(policyId, periodId)
  const isOffcyclePaymentsUrl = useMatchOffCyclesPaymentUrl(policyId, periodId)

  const offcyclesResultsUrl = navigation.payrollBundle.policy.period.offcycles.results.fullPath({
    policyId: policyId,
    periodId: periodId,
  })
  const offcyclesPaymentsUrl = navigation.payrollBundle.policy.period.offcycles.payments.fullPath({
    policyId: policyId,
    periodId: periodId,
  })

  if (isOffcyclePage) {
    // Show continue save and exit
    if (isOffcycleResultsUrl) {
      primaryAction = {
        disabled: false,
        onClick: () => navigate(offcyclesPaymentsUrl),
        label: i18n.t('payroll_bundle.policy_period.cycle_process.actions.next_step'),
      }

      secondaryAction = {
        disabled: false,
        onClick: () => navigate(dashboardUrl),
        label: i18n.t('payroll_bundle.policy_period.cycle_process.actions.exit_process'),
      }
    } else if (isOffcyclePaymentsUrl) {
      // show finish and previous button
      primaryAction = {
        disabled: false,
        onClick: () => navigate(dashboardUrl),
        label: i18n.t('payroll_bundle.policy_period.cycle_process.actions.finish_process'),
      }

      secondaryAction = {
        disabled: false,
        onClick: () => navigate(offcyclesResultsUrl),
        label: i18n.t('payroll_bundle.policy_period.cycle_process.actions.previous_step'),
      }
    }
  }

  return {
    primary: primaryAction,
    secondary: secondaryAction,
  }
}

export default useCyclesActions
